import ErrorSummary from '@/components/ErrorSummary'
import type { PublicEmailLoginInfo } from '@/hooks/usePublicEmail'
import { getLoginTypeDescription, usePublicEmailSignIn } from '@/hooks/usePublicEmail'
import { InfoOutlineIcon } from '@chakra-ui/icons'
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  Text,
  VStack
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import * as global from 'brokertslib/src/helpers/global'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import * as yup from 'yup'

const schema = yup.object({
  password: yup
    .string()
    .when('$isRcLoginType', {
      is: true,
      then: (s) =>
        s.required('Pro ověření identity zadejte požadované údaje.')
          .test(
            'rc-or-csp',
            'Rodné číslo musí obsahovat pouze číslice, nebo formát RRMMDDCSP pro cizince.',
            (value) => {
              if (!value) return false
              const trimmed = value.trim().toUpperCase()

              // Varianta 1: RČ jen číslice (9 nebo 10 znaků)
              const rcRegex = /^(\d{9,10}|\d{6}\/\d{3,4})$/

              // Varianta 2: CSP – 6 číslic + "CSP"
              const cspRegex = /^\d{6}CSP$/

              return rcRegex.test(trimmed) || cspRegex.test(trimmed)
            }
          ),
      otherwise: (s) => s.required('Pro ověření identity zadejte požadované údaje.'),
    })
})

export const EmailSignInForm = ({
  loginInfo,
  submitCallback,
}: {
  loginInfo: PublicEmailLoginInfo
  submitCallback: () => void
}) => {
  const { mutateAsync: signIn, isPending: isSubmitting } = usePublicEmailSignIn()

  const methods = useForm<{ password: string }>({
    mode: 'onChange',
    resolver: yupResolver(schema, {
      context: {
        isRcLoginType: loginInfo?.isRcLoginType
      }
    }) as any,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError
  } = methods

  const submitForm = handleSubmit(async (formValues) => {
    try {
      await signIn({
        idEmailMessage: loginInfo.idEmailMessage,
        paramsId: loginInfo.paramsId,
        password: formValues.password
      })
      submitCallback()
    } catch (err: any) {
      if (!(err.response && err.response.status === 400 && err.response.data)) return

      const data = err.response.data

      if (typeof data === 'string') {
        setError('password', { message: data })
        return
      }

      // server validation
      Object.keys(data).forEach((x: any) => {
        const key = global.toCamelCase(x) as any
        const val = data[x]

        if (val && val.length) {
          setError(key, { message: Array.isArray(val) ? val.join(' ') : val })
        }
      })
    }
  })

  const loginTypeDescription = getLoginTypeDescription(loginInfo.isRcLoginType, loginInfo.isForeigner)

  return (
    <FormProvider {...methods}>
      <VStack as="form" align="stretch" maxW='sm' onSubmit={submitForm} gap={6} alignSelf="center">
        <Heading as="h2" size="md" fontWeight="semibold">
          Přihlášení
        </Heading>

        <FormControl isInvalid={!!errors.password} isRequired>
          <HStack spacing={2} mb={1}>
            {loginInfo.isRcLoginType ? (
              <CustomTooltip
                label="Pro ověření identity zadejte Vaše RČ. Rodné číslo může obsahovat pouze číslice bez mezer nebo pomlček.
                            Cizí státní příslušníci (bez přiděleného RČ) zadají položku ve formátu RRMMDDCSP např. 7.1.1996 -> 960107CSP."
                trigger="clickHover"
                hasArrow
                placement="top"
                maxW={'300px'}
              >
                <Flex alignItems={'center'} gap={2}>
                  <Icon as={InfoOutlineIcon} boxSize="4" />
                  <FormLabel m="0">Pro ověření identity zadejte Vaše rodné číslo</FormLabel>
                </Flex>
              </CustomTooltip>
            ) : (
              <Flex alignItems={'center'} gap={2}>
                <FormLabel m="0">Pro ověření identity zadejte {loginTypeDescription}.</FormLabel>
              </Flex>
            )}
          </HStack>

          <Input
            {...register('password')}
            placeholder={loginInfo.isRcLoginType ? 'např. 850101/1234' : ''}
          />

          {loginInfo.isRcLoginType && (
            <Text mt={2} fontSize="sm" color={'gray.500'}>
              Formát: <code>######/###( # )</code> – lomítko je volitelné.
            </Text>
          )}
        </FormControl>

        <Button
          type="submit"
          size="lg"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
        >
          Uděluji souhlas
        </Button>

        <ErrorSummary errors={errors} />
      </VStack>
    </FormProvider>
  )
}
